import { ReleaseIdCard } from 'front-office/view-models/release-id-card';
import { DateTime } from 'luxon';
import { listReleaseMode } from 'shell/components/shared/release-mode';
import { choiceDatePDFFormatter } from 'shell/utils/choice-date-formatter';

export const transformIdCardRequest = (values: ReleaseIdCard, start: string) =>
  ({
    capelli: values.document?.hair,
    cartaPrecedente: values.document?.previousCard,
    cartaResa: values.document?.yieldCard === '1',
    comuneRilascio: values.document?.municipality?.description,
    dataRilascio: values.document?.releaseDate?.toFormat('yyyyMMdd'),
    dataScadenza: values.document?.expirationDate?.toFormat('yyyyMMdd'),
    dataScadenzaPrecendente: values.document?.expirationDateOld?.toFormat('yyyyMMdd'),
    italiano: values.isItalian,
    codicePersonaSipo: values?.dati3002?.codicePersonaSipo,
    datiIscrizione: values?.dati3002?.datiIscrizione,
    generalitaRichiedente: {
      annoEspatrio: values.registryOffice?.generality?.expatriationYear || null,
      codiceFiscale: {
        codFiscale: values.registryOffice?.generality?.fiscalCode || null,
        dataAttribuzioneValidita: values.registryOffice?.generality?.validityDateFiscalCode || null,
        descrizioneValiditaCF: values.registryOffice?.generality?.validityFiscalCode || null,
        validitaCF: values.registryOffice?.generality?.validityFiscalCodeId || null,
      },
      cognome: values.registryOffice?.generality?.surname || null,
      dataNascita: values.registryOffice?.generality?.birthDate || null,
      idSchedaSoggettoANPR: values.registryOffice?.generality?.anprIdentifier || null,
      idSchedaSoggettoComune: {
        idSchedaSoggettoComuneIstat:
          values.registryOffice?.generality?.municipalityIdentifier || null,
        idSchedaSoggetto: values.registryOffice?.generality?.istatCode || null,
      },
      luogoNascita: {
        comune: getComune(values),
        localita: getLocalita(values),
        luogoEccezionale: values.registryOffice?.generality?.exceptionalBirthPlace || null,
      },
      nome: values.registryOffice?.generality?.name || null,
      note: values.registryOffice?.generality?.note || null,
      senzaCognome: values.registryOffice?.generality?.withoutSurname ? 'S' : null,
      senzaGiorno: null,
      senzaGiornoMese: null,
      senzaNome: values.registryOffice?.generality?.withoutName ? 'S' : null,
      sesso: values.registryOffice?.generality?.gender || null,
      soggettoAIRE: values.registryOffice?.generality?.aire || null,
    },
    interdizioneEspatrio: values.document?.isExpatriationBan ? 'S' : 'N',
    messaggio: values.document?.stillValid || null,
    motivoRilascio: values.document?.releaseMode || null,
    numeroCarta: values?.document?.number
      ? `${values.document?.fixedLotNumber}${values?.document?.number}`
      : undefined,
    numeroInizioLotto: start || null,
    occhi: values.document?.eyes || null,
    provinciaRilascio: values.document?.municipality?.province || null,
    segniParticolari: values.document?.particularSigns || null,
    servizioDomicilio: values.document?.domicile === '1',
    stampaGenitori: values.document?.parentsPrint === '1',
    statura: values.document?.stature,
    tipoCarta: values.document?.type,
    tipoPagamento: values.document?.typePayment,
  } as const);

const getLocalita = (values: ReleaseIdCard) => {
  const localita = {
    codiceStato: values.registryOffice?.generality?.foreign?.municipality?.id || null,
    descrizioneLocalita:
      values.registryOffice?.generality?.foreign?.municipality?.description || null,
    descrizioneStato: values.registryOffice?.generality?.foreign?.state?.value || null,
    provinciaContea: values.registryOffice?.generality?.foreign?.state?.province || null,
  };
  return !localita.codiceStato &&
    !localita.descrizioneLocalita &&
    !localita.descrizioneStato &&
    !localita.provinciaContea
    ? null
    : localita;
};

const getComune = (values: ReleaseIdCard) => {
  const comune = {
    codiceIstat: values.registryOffice?.generality?.italian?.municipality?.id || null,
    descrizioneLocalita:
      values.registryOffice?.generality?.italian?.municipality?.description || null,
    nomeComune: values.registryOffice?.generality?.italian?.municipality?.description || null,
    siglaProvinciaIstat:
      values.registryOffice?.generality?.italian?.municipality?.provinceCode || null,
  };
  return !comune.codiceIstat &&
    !comune.descrizioneLocalita &&
    !comune.nomeComune &&
    !comune.siglaProvinciaIstat
    ? null
    : comune;
};

export const transformPrintTagRequestQuestura = (
  values: ReleaseIdCard,
  operatore: string,
  amount?: { importoDirittiFissi: number; importoDirittiSegreteria: number }
) =>
  ({
    stampaCartellino: {
      // attoMatrimonio: values.matrimonio?.datiMatrimonio.attoEvento?.numeroAtto,
      // attoNascita: values.attoNascita?.numeroAtto,
      annoRilascio: values.document?.releaseDate?.toFormat('yyyy'),
      interdizioneEspatrio: values.document?.isExpatriationBan ? 'S' : 'N',
      capelli: values.document?.hair,
      cdCertificato: 'CID01',
      indirizzo:
        values?.dati3002?.generalita?.soggettoAIRE === 'S'
          ? `abitante in ${values?.residence?.locationForeign || ''}-${
              values?.residence?.state || ''
            }\n\r${values?.residence?.nameForeign || ''} ${values?.residence?.civicNumber || ''}`
          : `abitante in ${values?.residence?.kind || ''} ${values?.residence?.name || ''}, ${
              values?.residence?.number || ''
            } ${values?.residence?.exponent || values?.residence?.letter ? '/' : ''} ${
              values?.residence?.exponent || ''
            } ${values?.residence?.letter || ''}`,
      attoNascita: `atto N. ${values?.attoNascita?.numeroAtto || ''}-${
        values?.attoNascita?.parte || ''
      }-${values?.attoNascita?.serie || ''}-${values?.attoNascita?.volume || ''}`,
      datiIscrizione: !values?.isResident
        ? `residente a ${values?.residence?.municipality || ''}`
        : '',
      nullaOsta: !values?.isResident
        ? `N.O.prot.n. ${values?.document?.numOfClearanceDocument || ''}`
        : '',
      stampaSC: values?.document?.printCivilStatus
        ? values?.document?.printCivilStatus === '1'
          ? 'SI'
          : 'NO'
        : null,
      // cittadinanza: values.registryOffice?.citizenship
      //   ? values.registryOffice?.citizenship[0].code
      //   : null,
      cognome: values.registryOffice?.generality?.surname,
      // comuneMatrimonio: values.matrimonio?.datiMatrimonio.luogoEvento?.comune?.nomeComune,
      // comuneProvenienza: values.datiIscrizione?.datiIscrizioneComune.comune.nomeComune,
      comuneRilascio: values.document?.municipality?.description,
      comuneRilascioPrec: values.document?.previousMunicipality?.nomeComune,
      // coniuge: `${values.matrimonio?.coniuge?.cognome} ${values.matrimonio?.coniuge?.nome}`,
      // dataIscrizione: values.datiIscrizione?.datiIscrizioneComune?.dataIscrizione
      //   ? DateTime.fromISO(values.datiIscrizione?.datiIscrizioneComune.dataIscrizione).toFormat(
      //       'dd/MM/yyyy'
      //     )
      //   : null,
      // dataMatrimonio: values.matrimonio?.datiMatrimonio?.dataEvento
      //   ? DateTime.fromISO(values.matrimonio?.datiMatrimonio.dataEvento).toFormat('dd/MM/yyyy')
      //   : null,
      // dataNascita: values.registryOffice?.generality?.birthDate?.toFormat('dd/MM/yyyy'),
      dataRilascioCi: values.document?.releaseDate?.toFormat('dd/MM/yyyy'),
      dirittiFissi: amount?.importoDirittiFissi,
      dirittiSegreteria: amount?.importoDirittiSegreteria,
      // luogoNascita:
      //   values.registryOffice?.generality?.foreign?.municipality?.description ||
      //   values.registryOffice?.generality?.italian?.municipality?.description,
      // maternita: `${values.maternita?.generalita.cognome} ${values.maternita?.generalita.nome}`,
      motivoRilascio:
        listReleaseMode.find((item) => values.document?.releaseMode === item.id)?.value || null,
      nome: values.registryOffice?.generality?.name,
      numeroCI: values?.document?.number
        ? `${values.document?.fixedLotNumber}${values?.document?.number}`
        : undefined,
      numeroCIPrec: values.document?.previousCard,
      occhi: values.document?.eyes,
      operatore: operatore,
      // paternita: `${values.paternita?.generalita.cognome} ${values.paternita?.generalita.nome}`,
      professione:
        values.otherData?.professionDescription?.value ||
        values.otherData?.professionalPositionItem?.value,
      resaSiNo: values.document?.yieldCard,
      segniParticolari: values.document?.particularSigns,
      // statoCivile: values.statoCivile?.statoCivile,
      statura: values.document?.stature,
    },
    dati3002: values.dati3002,
  } as const);

export const transformPrintTagRequestComune = (values: ReleaseIdCard, operatore: string) =>
  ({
    stampaCartellino: {
      // attoMatrimonio: values.matrimonio?.datiMatrimonio.attoEvento?.numeroAtto,
      // attoNascita: values.attoNascita?.numeroAtto,
      annoRilascio: values.document?.releaseDate?.toFormat('yyyy'),
      interdizioneEspatrio: values.document?.isExpatriationBan ? 'S' : 'N',
      capelli: values.document?.hair,
      cdCertificato: 'CIE01',
      indirizzo:
        values?.dati3002?.generalita?.soggettoAIRE === 'S'
          ? `abitante in ${values?.residence?.locationForeign || ''}-${
              values?.residence?.state || ''
            }\n\r${values?.residence?.nameForeign || ''} ${values?.residence?.civicNumber || ''}`
          : `abitante in ${values?.residence?.kind || ''} ${values?.residence?.name || ''}, ${
              values?.residence?.number || ''
            } ${values?.residence?.exponent || values?.residence?.letter ? '/' : ''} ${
              values?.residence?.exponent || ''
            } ${values?.residence?.letter || ''}`,
      attoNascita: `atto N. ${values?.attoNascita?.numeroAtto || ''}-${
        values?.attoNascita?.parte || ''
      }-${values?.attoNascita?.serie || ''}-${values?.attoNascita?.volume || ''}`,
      datiIscrizione: !values?.isResident
        ? `residente a ${values?.residence?.municipality || ''}`
        : '',
      nullaOsta: !values?.isResident
        ? `N.O.prot.n. ${values?.document?.numOfClearanceDocument || ''}`
        : '',
      // cittadinanza: values.registryOffice?.citizenship
      //   ? values.registryOffice?.citizenship[0].code
      //   : null,
      cognome: values.registryOffice?.generality?.surname,
      // comuneMatrimonio: values.matrimonio?.datiMatrimonio.luogoEvento?.comune?.nomeComune,
      // comuneProvenienza: values.datiIscrizione?.datiIscrizioneComune.comune.nomeComune,
      comuneRilascio: values.document?.municipality?.description,
      comuneRilascioPrec: values.document?.previousMunicipality?.nomeComune,
      // coniuge: `${values.matrimonio?.coniuge?.cognome} ${values.matrimonio?.coniuge?.nome}`,
      // dataIscrizione: values.datiIscrizione?.datiIscrizioneComune?.dataIscrizione
      //   ? DateTime.fromISO(values.datiIscrizione?.datiIscrizioneComune.dataIscrizione).toFormat(
      //       'dd/MM/yyyy'
      //     )
      //   : null,
      // dataMatrimonio: values.matrimonio?.datiMatrimonio?.dataEvento
      //   ? DateTime.fromISO(values.matrimonio?.datiMatrimonio.dataEvento).toFormat('dd/MM/yyyy')
      //   : null,
      // dataNascita: values.registryOffice?.generality?.birthDate?.toFormat('dd/MM/yyyy'),
      dataRilascioCi: values.document?.releaseDate?.toFormat('dd/MM/yyyy'),
      // luogoNascita:
      //   values.registryOffice?.generality?.foreign?.municipality?.description ||
      //   values.registryOffice?.generality?.italian?.municipality?.description,
      // maternita: `${values.maternita?.generalita.cognome} ${values.maternita?.generalita.nome}`,
      motivoRilascio:
        listReleaseMode.find((item) => values.document?.releaseMode === item.id)?.value || null,
      nome: values.registryOffice?.generality?.name,
      numeroCI: values?.document?.number
        ? `${values.document?.fixedLotNumber}${values?.document?.number}`
        : undefined,
      numeroCIPrec: values.document?.previousCard,
      occhi: values.document?.eyes,
      operatore: operatore,
      // paternita: `${values.paternita?.generalita.cognome} ${values.paternita?.generalita.nome}`,
      professione:
        values.otherData?.professionDescription?.value ||
        values.otherData?.professionalPositionItem?.value,
      resaSiNo: values.document?.yieldCard,
      segniParticolari: values.document?.particularSigns,
      // statoCivile: values.statoCivile?.statoCivile,
      statura: values.document?.stature,
    },
    dati3002: values.dati3002,
  } as const);

export const transformInternalPrintRequest = (
  values: ReleaseIdCard,
  operatore: string,
  amount: number
) =>
  ({
    attoNumero: values.attoNascita?.numeroAtto,
    attoParte: values.attoNascita?.parte,
    attoSerie: values.attoNascita?.serie,
    provinciaStatoNascita:
      values.registryOffice?.generality?.foreign?.municipality?.province ||
      values.registryOffice?.generality?.italian?.municipality?.province,
    capelli: values.document?.hair,
    cdCertificato: 'CID02',
    sesso: values?.dati3002?.generalita?.sesso,
    cittadinanza: (values.dati3002?.cittadinanza || [])[0]?.codiceStato,
    cognome: values.registryOffice?.generality?.surname,
    comuneResidenza:
      values?.dati3002?.generalita?.soggettoAIRE === 'S'
        ? values.residence?.locationForeign
        : values.residence?.municipality,
    dataNascita: choiceDatePDFFormatter(
      values.registryOffice?.generality?.birthDate,
      values.registryOffice?.generality?.birthChoiceDate
    ),
    dataRilascio: values.document?.releaseDate?.toFormat('dd/MM/yyyy'),
    diritti: amount?.toString(),
    indirizzoResidenza:
      values?.dati3002?.generalita?.soggettoAIRE !== 'S'
        ? `${values.residence?.kind || ''} ${values.residence?.name} ${
            values.residence?.number || ''
          } ${values?.residence?.exponent || values?.residence?.letter ? '/' : ''} ${
            values?.residence?.exponent || ''
          }${values?.residence?.letter || ''}`
        : `${values?.residence?.nameForeign || ''} ${values?.residence?.civicNumber || ''}`,
    luogoNascita:
      values.registryOffice?.generality?.foreign?.municipality?.description ||
      values.registryOffice?.generality?.italian?.municipality?.description,
    nome: values.registryOffice?.generality?.name,
    occhi: values.document?.eyes,
    operatore: operatore,
    professione:
      values.otherData?.professionDescription?.value ||
      values.otherData?.professionalPositionItem?.value,
    segniparticolari: values.document?.particularSigns,
    statoCivile: values.statoCivile?.statoCivile,
    statura: values.document?.stature,
    stampaSC: values?.document?.printCivilStatus
      ? values?.document?.printCivilStatus === '1'
        ? 'SI'
        : 'NO'
      : null,
  } as const);

export const transformExternalPrintRequest = (values: ReleaseIdCard) =>
  ({
    cdCertificato: 'CID03',
    cognomeMadre:
      values.document?.showParents && values.document?.parentsPrint === '1'
        ? values?.maternita?.generalita?.cognome
        : undefined,
    cognomePadre:
      values.document?.showParents && values.document?.parentsPrint === '1'
        ? values?.paternita?.generalita?.cognome
        : undefined,
    cognomeRichiedente: values?.registryOffice?.generality?.surname,
    comuneRilascio: values?.document?.municipality?.description,
    dataScadenza: values.document?.expirationDate?.toFormat('dd/MM/yyyy'),
    nomeMadre:
      values.document?.showParents && values.document?.parentsPrint === '1'
        ? values?.maternita?.generalita?.nome
        : undefined,
    nomePadre:
      values.document?.showParents && values.document?.parentsPrint === '1'
        ? values?.paternita?.generalita?.nome
        : undefined,
    nomeRichiedente: values.registryOffice?.generality?.name,
    interdizioneEspatrio: values.document?.isExpatriationBan ? 'S' : 'N',
  } as const);

export const transformEnvelopePrintRequest = (values: ReleaseIdCard) =>
  ({
    // cdCertificato: 'CID04',
    cognome: values.registryOffice?.generality?.surname,
    nome: values.registryOffice?.generality?.name,
    dataRilascio: DateTime.now().toFormat('dd/MM/yyyy'),
    numeroCartaIdentita: values?.document?.number
      ? `${values.document?.fixedLotNumber}${values?.document?.number}`
      : undefined,
  } as const);
